// ODS-EWO-033 P4. The Context Bridge in the chrome: while a customer is looking
// at one business object, the shell can say what else in Studio is attached to
// it, without that object's application knowing anything about the others.
//
// It appears only where an application declared that the route addresses an
// object it owns (`ApplicationManifest.subjects`). The shell cannot infer that a
// path segment is an identifier, and asking the bridge about something no
// application claims returns a refusal rather than context.

import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Badge } from "@odingard/design-system";
import type { AttentionBridge } from "@odingard/sdk-client";
import { shellAttentionApi } from "../lib/api";
import { APPLICATION_MANIFESTS, platformApplicationOf, subjectForPath } from "./applications";

type State =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "answered"; bridge: AttentionBridge }
  | { kind: "unavailable" };

function label(value: string): string {
  return value.replace(/[_-]+/g, " ");
}

function applicationName(application: string): string {
  const manifest = APPLICATION_MANIFESTS.find(
    (m) => platformApplicationOf(m) === application && !m.surfaceOf,
  );
  return manifest?.name ?? label(application);
}

export function ContextIndicator({ orgId }: { orgId: string }) {
  const location = useLocation();
  const subject = subjectForPath(location.pathname);
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<State>({ kind: "idle" });
  const container = useRef<HTMLDivElement>(null);

  const subjectType = subject?.subjectType ?? "";
  const subjectId = subject?.subjectId ?? "";

  useEffect(() => {
    setOpen(false);
    setState({ kind: "idle" });
  }, [subjectType, subjectId]);

  useEffect(() => {
    if (!open || !subjectType || !subjectId) return;
    if (!orgId) {
      setState({ kind: "unavailable" });
      return;
    }
    let live = true;
    setState({ kind: "loading" });
    shellAttentionApi
      .bridge(orgId, subjectType, subjectId)
      .then((response) => {
        if (!live) return;
        if (response.mode === "unavailable" || !response.data) {
          setState({ kind: "unavailable" });
          return;
        }
        setState({ kind: "answered", bridge: response.data });
      })
      .catch(() => {
        if (live) setState({ kind: "unavailable" });
      });
    return () => {
      live = false;
    };
  }, [open, orgId, subjectType, subjectId]);

  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // Nothing is in view that any application claims as an object, so there is no
  // context to offer. A control that leads to a refusal is worse than none.
  if (!subject) return null;

  const related = state.kind === "answered" ? state.bridge.related : [];

  return (
    <div className="context-indicator" ref={container}>
      <button
        type="button"
        className="ods-btn ods-btn--ghost ods-btn--sm"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(event) => {
          if (event.key === "Escape") setOpen(false);
        }}
      >
        <span aria-hidden="true">🔗</span> Related context
      </button>
      {open && (
        <div className="context-indicator__panel" role="dialog" aria-label="Related context">
          <p className="context-indicator__subject">
            {label(subject.subjectType)} in {subject.manifest.name}
          </p>
          {state.kind === "loading" && (
            <p className="context-indicator__note">Asking the Context Bridge…</p>
          )}
          {state.kind === "unavailable" && (
            <p className="context-indicator__note">
              We could not ask the Context Bridge. Nothing here means nothing is
              known, not that nothing is related.
            </p>
          )}
          {state.kind === "answered" && related.length === 0 && (
            <p className="context-indicator__note">
              No other application holds anything related to this object.
            </p>
          )}
          {state.kind === "answered" && related.length > 0 && (
            <ul className="context-indicator__list">
              {related.map((entry) => {
                const unavailable = entry.state === "unavailable" || entry.count === null;
                return (
                  <li key={`${entry.application}:${entry.objectType}:${entry.basis}`}>
                    <span className="context-indicator__type">{label(entry.objectType)}</span>
                    <span className="context-indicator__meta">
                      {applicationName(entry.application)} ·{" "}
                      {entry.basis === "derived" ? "derived" : "recorded"}
                    </span>
                    <Badge tone={unavailable ? "warning" : "neutral"}>
                      {unavailable ? "Unavailable" : String(entry.count)}
                    </Badge>
                  </li>
                );
              })}
            </ul>
          )}
          <Link
            className="context-indicator__all"
            to={`/studio/context/${encodeURIComponent(subject.subjectType)}/${encodeURIComponent(subject.subjectId)}`}
            onClick={() => setOpen(false)}
          >
            Open the full context
          </Link>
        </div>
      )}
    </div>
  );
}
